import { useLayoutEffect, useRef } from "react";
import { Row } from "../../../types";

const useSyncScroll = (rows: Row[]) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const stickyCols = useRef<HTMLDivElement[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const wasZero = useRef(rows.length === 0);
  const renderTimes = useRef(0);

  useLayoutEffect(() => {
    const isZero = rows.length === 0;

    if (renderTimes.current !== 0 && isZero === wasZero.current) return;
    renderTimes.current++;

    wasZero.current = isZero;

    let lastScrollTop = 0;
    let lastScrollLeft = 0;

    const syncVerticalScroll = (scrollTop: number) => {
      stickyCols.current.forEach((col) => {
        if (col) col.scrollTop = scrollTop;
      });
      if (bodyRef.current) bodyRef.current.scrollTop = scrollTop;
    };

    const syncHorizontalScroll = (scrollLeft: number) => {
      if (contentRef.current) {
        contentRef.current.scrollLeft = scrollLeft;
      }
      if (bodyRef.current) {
        bodyRef.current.scrollLeft = scrollLeft;
      }
    };

    const attachScrollListeners = () => {
      contentRef.current?.addEventListener("scroll", () => {
        const scrollLeft = contentRef.current!.scrollLeft;
        if (scrollLeft !== lastScrollLeft) {
          syncHorizontalScroll(scrollLeft);
          lastScrollLeft = scrollLeft;
        }
      });

      bodyRef.current?.addEventListener("scroll", () => {
        const scrollTop = bodyRef.current!.scrollTop;
        const scrollLeft = bodyRef.current!.scrollLeft;

        if (scrollTop !== lastScrollTop) {
          syncVerticalScroll(scrollTop);
          lastScrollTop = scrollTop;
        }

        if (scrollLeft !== lastScrollLeft) {
          syncHorizontalScroll(scrollLeft);
          lastScrollLeft = scrollLeft;
        }
      });

      stickyCols.current.forEach((col) => {
        col?.addEventListener("scroll", () => {
          const scrollTop = col.scrollTop;
          if (scrollTop !== lastScrollTop) {
            syncVerticalScroll(scrollTop);
            lastScrollTop = scrollTop;
          }
        });
      });
    };

    setTimeout(attachScrollListeners, 300);

    return () => {
      contentRef.current?.removeEventListener("scroll", () => {
        if (!contentRef.current) return;
        const scrollLeft = contentRef.current?.scrollLeft;
        syncHorizontalScroll(scrollLeft);
      });

      bodyRef.current?.removeEventListener("scroll", () => {
        if (!bodyRef.current) return;
        const scrollTop = bodyRef.current?.scrollTop;
        const scrollLeft = bodyRef.current?.scrollLeft;
        syncVerticalScroll(scrollTop);
        syncHorizontalScroll(scrollLeft);
      });

      stickyCols.current.forEach((col) => {
        col?.removeEventListener("scroll", () => {
          const scrollTop = col.scrollTop;
          syncVerticalScroll(scrollTop);
        });
      });
    };
  }, [rows.length]);

  return {
    contentRef,
    bodyRef,
    stickyCols,
  };
};

export default useSyncScroll;
